import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Tooltip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, PageContent, ViiluDataGrid, api } from '@viilutalous/common';
import { useTenant } from '../contexts/TenantContext';
import { useMeHost } from '../contexts/MeHostContext';

/**
 * /me/companies — tenants the user can access under the selected My Desktop host.
 * Double-click a row to switch tenant + open it.
 */
export default function MyCompanies() {
  const { t } = useTranslation();
  const navigate = useRouterNavigate();
  const { currentTenant, selectTenant } = useTenant();
  const { hostId, hosts } = useMeHost();

  // Host-scoped list (including inactive) — /user/tenants?host_id= caps to the
  // selected My Desktop host. useTenant() is the unfiltered login list.
  const [allTenants, setAllTenants] = useState([]);
  useEffect(() => {
    if (hosts.length > 0 && !hostId) {
      setAllTenants([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/user/tenants', {
      params: {
        include_inactive: true,
        ...(hostId ? { host_id: hostId } : {}),
      },
    })
      .then((res) => { if (!cancelled) setAllTenants(res.data?.tenants || []); })
      .catch((err) => { console.error('Failed to load tenants', err); });
    return () => { cancelled = true; };
  }, [hostId, hosts.length]);

  const rows = useMemo(
    () => allTenants.map((tnt) => ({
      id: tnt.id,
      host_name: tnt.host_name || '',
      tenant_name: tnt.display_name || tnt.name || '',
      slug: tnt.slug || '',
      is_active: tnt.is_active !== false,
      is_current: currentTenant?.id === tnt.id,
      _tenant: tnt,
    })),
    [allTenants, currentTenant?.id],
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'is_current',
        headerName: '',
        width: 48,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (p) => (p.value
          ? (
            <Tooltip title={t('me.companies.current', 'Current')}>
              <CheckCircle fontSize="small" color="primary" />
            </Tooltip>
          )
          : null),
      },
      { field: 'tenant_name', headerName: t('me.companies.tenant', 'Tenant'), flex: 2, minWidth: 220 },
      { field: 'host_name', headerName: t('me.companies.host', 'Host'), flex: 1, minWidth: 180 },
      {
        field: 'slug',
        headerName: t('me.companies.slug', 'Slug'),
        flex: 1,
        minWidth: 180,
        cellStyle: { fontFamily: 'monospace' },
      },
      {
        field: 'is_active',
        headerName: t('me.companies.status', 'Status'),
        width: 120,
        cellRenderer: (p) => (
          <Chip
            size="small"
            label={p.value
              ? t('me.companies.active', 'Active')
              : t('me.companies.inactive', 'Inactive')}
            color={p.value ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
    ],
    [t],
  );

  const openTenant = (row) => {
    const tnt = row?._tenant;
    if (!tnt?.slug || row?.is_active === false) return;
    selectTenant(tnt);
    navigate(`/app/${tnt.slug}/dashboard`);
  };

  return (
    <>
      <PageHeader />
      <PageContent>
        {allTenants.length === 0 && (
          <Alert severity="info">{t('me.companies.empty', 'You have no tenant access yet.')}</Alert>
        )}
        {allTenants.length > 0 && (
          <ViiluDataGrid
            gridId="me-companies"
            rowData={rows}
            columnDefs={columnDefs}
            onRowDoubleClicked={(e) => openTenant(e.data)}
            showToolbar={false}
            hideFooter
            height="calc(100vh - 180px)"
          />
        )}
      </PageContent>
    </>
  );
}
